import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Building2, KeyRound, MoreHorizontal, Pencil, Plus, Trash2, Users } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  Avatar,
  DataTable,
  DetailList,
  DetailSection,
  DetailSheet,
  EmptyState,
  IconTile,
  Pagination,
  Panel,
  SearchInput,
  Segmented,
  SkeletonRows,
  Tag,
  Toolbar,
  fmtDateBR,
  relativeDays,
  rowClass,
  td,
  th,
  usePagination,
} from "@/components/superadmin/kit"
import { useCondominiums, useDeleteCondominium, useSetCondominiumModules } from "@/hooks/useCondominiums"
import { useLicenses } from "@/hooks/useLicenses"
import { useProfiles } from "@/hooks/useProfiles"
import { ALL_MODULES, MODULE_META, moduleIncluded, resolveOrgModules, type AppModule } from "@/lib/modules"
import { cn } from "@/lib/utils"
import { CondominiumWithSubscription } from "@/types/superadmin"

import { CreateCondominiumModal } from "./CreateCondominiumModal"
import { EditCondominiumModal } from "./EditCondominiumModal"
import { ModulePicker, ModuleTags } from "./ModulePicker"
import { ORG_TYPE_LABEL, PAYMENT_STATUS } from "./PlatformOverview"
import { ROLE_META } from "./UserManagement"

type StatusFilter = "all" | "active" | "attention" | "suspended"

const CondominiumManagement = () => {
  const { data: condominiums = [], isLoading } = useCondominiums()
  const { data: profiles = [] } = useProfiles()
  const { data: licenses = [] } = useLicenses()
  const deleteMutation = useDeleteCondominium()
  const modulesMutation = useSetCondominiumModules()
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<CondominiumWithSubscription | null>(null)
  const [viewingId, setViewingId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<CondominiumWithSubscription | null>(null)
  const [query, setQuery] = useState("")
  const [status, setStatus] = useState<StatusFilter>("all")
  const [moduleFilter, setModuleFilter] = useState<"all" | AppModule>("all")
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    if (searchParams.get("nova") !== "1") return
    setCreateOpen(true)
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])

  const usersByOrg = useMemo(() => {
    const map = new Map<string, typeof profiles>()
    for (const p of profiles) {
      if (!p.condominium_id) continue
      map.set(p.condominium_id, [...(map.get(p.condominium_id) ?? []), p])
    }
    return map
  }, [profiles])

  const counts = useMemo(() => ({
    all: condominiums.length,
    active: condominiums.filter((c) => c.payment_status === "active").length,
    attention: condominiums.filter((c) => c.payment_status === "overdue" || c.payment_status === "pending").length,
    suspended: condominiums.filter((c) => c.payment_status === "suspended").length,
  }), [condominiums])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return condominiums.filter((c) => {
      const matchesQuery = !q || [c.name, c.address].some((v) => String(v || "").toLowerCase().includes(q))
      const matchesStatus =
        status === "all" ||
        (status === "attention" ? c.payment_status === "overdue" || c.payment_status === "pending" : c.payment_status === status)
      const matchesModule = moduleFilter === "all" || moduleIncluded(resolveOrgModules(c), moduleFilter)
      return matchesQuery && matchesStatus && matchesModule
    })
  }, [condominiums, query, status, moduleFilter])

  const pager = usePagination(filtered, 12)
  useEffect(() => pager.reset(), [query, status, moduleFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  // Derivado da lista para refletir na hora o que o superadmin salva no painel.
  const viewing = viewingId ? condominiums.find((c) => c.id === viewingId) ?? null : null
  const setViewing = (c: CondominiumWithSubscription | null) => setViewingId(c?.id ?? null)

  const viewingUsers = viewing ? usersByOrg.get(viewing.id) ?? [] : []
  const viewingLicenses = viewing ? licenses.filter((l) => l.condominium_id === viewing.id || l.company?.condominium_id === viewing.id) : []

  return (
    <>
      <Panel flush>
        <Toolbar>
          <Segmented
            value={status}
            onChange={setStatus}
            options={[
              { value: "all", label: "Todas", count: counts.all },
              { value: "active", label: "Em dia", count: counts.active },
              { value: "attention", label: "Atenção", count: counts.attention },
              { value: "suspended", label: "Suspensas", count: counts.suspended },
            ]}
          />
          <div className="flex flex-col gap-2 sm:flex-row lg:ml-auto">
            <Select value={moduleFilter} onValueChange={(v) => setModuleFilter(v as "all" | AppModule)}>
              <SelectTrigger className="h-8 w-full text-[13px] sm:w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os módulos</SelectItem>
                {ALL_MODULES.map((m) => <SelectItem key={m} value={m}>{MODULE_META[m].short}</SelectItem>)}
              </SelectContent>
            </Select>
            <SearchInput value={query} onChange={setQuery} placeholder="Buscar por nome ou endereço" />
          </div>
        </Toolbar>

        {isLoading ? (
          <SkeletonRows />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Building2}
            title={condominiums.length ? "Nenhuma organização neste filtro" : "Nenhuma organização ainda"}
            description={condominiums.length ? "Ajuste a busca, o status ou o módulo." : "Cadastre o primeiro condomínio ou empresa."}
            action={!condominiums.length && <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5"><Plus className="h-4 w-4" />Nova organização</Button>}
          />
        ) : (
          <>
            <DataTable minWidth={820}>
              <thead>
                <tr className="border-b border-border">
                  <th className={th}>Organização</th>
                  <th className={th}>Módulos</th>
                  <th className={cn(th, "text-right")}>Usuários</th>
                  <th className={th}>Pagamento</th>
                  <th className={th}>Assinatura até</th>
                  <th className={cn(th, "w-px")}><span className="sr-only">Ações</span></th>
                </tr>
              </thead>
              <tbody>
                {pager.slice.map((c) => {
                  const st = PAYMENT_STATUS[c.payment_status] ?? PAYMENT_STATUS.active
                  const users = usersByOrg.get(c.id)?.length ?? 0
                  const rel = relativeDays(c.subscription_expires_at)
                  const expired = c.subscription_expires_at && new Date(c.subscription_expires_at) < new Date()
                  return (
                    <tr key={c.id} className={rowClass} onClick={() => setViewing(c)}>
                      <td className={td}>
                        <div className="flex items-center gap-3">
                          <IconTile icon={Building2} />
                          <div className="min-w-0">
                            <div className="max-w-[280px] truncate text-[13px] font-medium text-foreground">{c.name}</div>
                            <div className="max-w-[280px] truncate text-[12px] text-muted-foreground">{c.address || "Sem endereço"}</div>
                          </div>
                        </div>
                      </td>
                      <td className={td}><ModuleTags modules={resolveOrgModules(c)} /></td>
                      <td className={cn(td, "text-right text-[13px] tabular-nums text-foreground")}>{users}</td>
                      <td className={td}><Tag tone={st.tone} dot>{st.label}</Tag></td>
                      <td className={td}>
                        <div className="text-[13px] tabular-nums text-foreground">{c.subscription_expires_at ? fmtDateBR(c.subscription_expires_at) : <span className="text-muted-foreground">Sem prazo</span>}</div>
                        {rel && <div className={cn("text-[11px]", expired ? "text-[hsl(358_90%_72%)]" : "text-muted-foreground")}>{expired ? `venceu ${rel}` : rel}</div>}
                      </td>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" aria-label="Ações"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onSelect={() => setViewing(c)}><Building2 className="mr-2 h-4 w-4" />Ver detalhes</DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => setEditing(c)}><Pencil className="mr-2 h-4 w-4" />Editar</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onSelect={() => setDeleting(c)} className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"><Trash2 className="mr-2 h-4 w-4" />Excluir</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </DataTable>
            <Pagination {...pager} noun="organizações" />
          </>
        )}
      </Panel>

      <DetailSheet
        open={!!viewing}
        onOpenChange={(o) => !o && setViewing(null)}
        badge={viewing && <Tag tone={(PAYMENT_STATUS[viewing.payment_status] ?? PAYMENT_STATUS.active).tone} dot className="self-start">{(PAYMENT_STATUS[viewing.payment_status] ?? PAYMENT_STATUS.active).label}</Tag>}
        title={viewing?.name}
        subtitle={viewing && `${ORG_TYPE_LABEL[viewing.type] ?? "Condomínio"} · cadastrada em ${fmtDateBR(viewing.created_at)}`}
        actions={viewing && (
          <>
            <Button size="sm" className="h-8 gap-1.5" onClick={() => { setEditing(viewing); setViewing(null) }}><Pencil className="h-3.5 w-3.5" />Editar</Button>
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-[hsl(var(--destructive))]" onClick={() => setDeleting(viewing)}><Trash2 className="h-3.5 w-3.5" />Excluir</Button>
          </>
        )}
      >
        {viewing && (
          <>
            <DetailSection title="Cadastro">
              <DetailList items={[
                ["Endereço", viewing.address],
                ["Tipo", ORG_TYPE_LABEL[viewing.type] ?? "Condomínio"],
                ["Unidades", viewing.total_units ?? null],
                ["Assinatura até", viewing.subscription_expires_at ? `${fmtDateBR(viewing.subscription_expires_at)} (${relativeDays(viewing.subscription_expires_at)})` : "Sem prazo definido"],
                ["Atualizada em", fmtDateBR(viewing.updated_at, true)],
                ["ID", <code key="id" className="font-mono text-[11px] text-muted-foreground">{viewing.id}</code>],
              ]} />
            </DetailSection>

            <DetailSection
              title="Módulos"
              action={modulesMutation.isPending && <span className="text-[11px] text-muted-foreground">Salvando…</span>}
            >
              <ModulePicker
                value={resolveOrgModules(viewing)}
                disabled={modulesMutation.isPending}
                onChange={(modules) => modulesMutation.mutate({ id: viewing.id, modules })}
              />
              <p className="mt-2 text-[12px] text-muted-foreground">
                Define os menus, rotas e dados que os usuários desta organização acessam. O tipo ({ORG_TYPE_LABEL[viewing.type] ?? "Condomínio"}) é só o preset do cadastro.
              </p>
            </DetailSection>

            <DetailSection title={`Usuários (${viewingUsers.length})`}>
              {viewingUsers.length === 0 ? (
                <p className="rounded-lg border border-border p-3 text-[13px] text-muted-foreground">Nenhum usuário vinculado.</p>
              ) : (
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {viewingUsers.slice(0, 8).map((u) => {
                    const role = ROLE_META[u.role] ?? { label: u.role, tone: "neutral" as const }
                    return (
                      <li key={u.id} className="flex items-center gap-3 px-3 py-2">
                        <Avatar name={u.full_name || u.email} className="h-7 w-7 text-[10px]" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[13px] text-foreground">{u.full_name || "Sem nome"}</div>
                          <div className="truncate text-[11px] text-muted-foreground">{u.email}</div>
                        </div>
                        <Tag tone={role.tone}>{role.label}</Tag>
                      </li>
                    )
                  })}
                  {viewingUsers.length > 8 && <li className="px-3 py-2 text-[12px] text-muted-foreground"><Users className="mr-1.5 inline h-3.5 w-3.5" />e mais {viewingUsers.length - 8}</li>}
                </ul>
              )}
            </DetailSection>

            <DetailSection title={`Licenças (${viewingLicenses.length})`}>
              {viewingLicenses.length === 0 ? (
                <p className="rounded-lg border border-border p-3 text-[13px] text-muted-foreground">Nenhuma licença desktop vinculada.</p>
              ) : (
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {viewingLicenses.map((l) => (
                    <li key={l.id} className="flex items-center gap-3 px-3 py-2 text-[13px]">
                      <KeyRound className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1 truncate text-foreground">{l.plan}</span>
                      <span className="text-[12px] text-muted-foreground">{fmtDateBR(l.expires_at || l.expiration_date)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </DetailSection>
          </>
        )}
      </DetailSheet>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir "{deleting?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>Todos os dados ligados a esta organização (usuários, registros, histórico) serão removidos. Não dá para desfazer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90"
              onClick={async () => {
                if (!deleting) return
                try {
                  await deleteMutation.mutateAsync(deleting.id)
                  setViewing(null)
                } catch (error) {
                  console.error("Erro ao deletar organização:", error)
                }
              }}
            >
              {deleteMutation.isPending ? "Excluindo…" : "Excluir organização"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CreateCondominiumModal isOpen={createOpen} onClose={() => setCreateOpen(false)} />
      <EditCondominiumModal isOpen={!!editing} onClose={() => setEditing(null)} condominium={editing} />
    </>
  )
}

export { CondominiumManagement }
